// ============================================================
// Client Alerts
// Pure functions that compute content-cadence alerts for clients.
// Used by ClientProfile, ClientProfileFull, BlogAssignments, SocialBatches.
// ============================================================

/**
 * Returns the completion date (ISO string) of the most recent finished item.
 * For blogs: looks at blog_assignments.status === 'complete' and uses updated_at.
 * For social: looks at social_batches.status === 'complete' and uses updated_at.
 * Returns null if nothing has ever been completed.
 */
function lastCompletedAt(items) {
  const completed = items.filter(i => i.status === 'complete')
  if (completed.length === 0) return null
  // Use updated_at (set by the trigger when status flipped to complete)
  const sorted = completed.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
  return sorted[0].updated_at
}

/**
 * Returns true if any item is currently in flight (status not 'complete').
 */
function hasInFlight(items) {
  return items.some(i => i.status !== 'complete')
}

/**
 * Days between an ISO date string and today.
 */
function daysSince(isoDate) {
  if (!isoDate) return null
  const then = new Date(isoDate)
  if (isNaN(then.getTime())) return null
  const now = new Date()
  return Math.floor((now - then) / (1000 * 60 * 60 * 24))
}

/**
 * Compute alerts for a single client.
 * @param {object} client - the client row
 * @param {object[]} clientBlogs - blog_assignments rows for this client
 * @param {object[]} clientBatches - social_batches rows for this client
 * @param {object[]} clientAudits - seo_projects rows where template_id = 'tpl-quarterly-audit'
 * @returns {object[]} alerts: [{ id, type, level, title, message, action }]
 */
export function computeClientAlerts(client, clientBlogs = [], clientBatches = [], clientAudits = []) {
  const alerts = []

  // ── BLOG ALERT ──────────────────────────────────────────────────────────
  // Only for clients whose plan includes blogs. Mirrors the Plan tab, where the
  // "SEO / Blogs" card counts as active with either the SEO or Blogs service.
  const hasBlogService = !!(client.has_blogs || client.has_seo)
  if (hasBlogService && clientBlogs.length > 0 && !hasInFlight(clientBlogs)) {
    const lastDate = lastCompletedAt(clientBlogs)
    const days = daysSince(lastDate)
    if (days !== null && days >= 14) {
      alerts.push({
        id: `blog-${client.id}`,
        type: 'blog_cadence',
        level: days >= 21 ? 'warning' : 'info',
        title: 'Time to brief the next blog',
        message: `It's been ${days} days since the last blog wrapped — keep the cadence going.`,
        action: 'open_blogs',
      })
    }
  }

  // ── SOCIAL ALERT ────────────────────────────────────────────────────────
  // Only for clients whose plan includes social (same flag as the Plan tab's
  // "Social Media" card). Past batches alone don't mean social is still active.
  if (client.has_social && clientBatches.length > 0 && !hasInFlight(clientBatches)) {
    const lastDate = lastCompletedAt(clientBatches)
    const days = daysSince(lastDate)
    if (days !== null && days >= 16) {
      alerts.push({
        id: `social-${client.id}`,
        type: 'social_cadence',
        level: days >= 25 ? 'warning' : 'info',
        title: "Time to brief next month's social batch",
        message: `Last batch wrapped ${days} days ago — the scheduled posts are running out.`,
        action: 'open_social',
      })
    }
  }

  // ── QUARTERLY AUDIT ALERT ──────────────────────────────────────────────
  // Audits are seo_projects with template_id = 'tpl-quarterly-audit'.
  // Suppress if one is currently in flight.
  if (clientAudits && clientAudits.length > 0) {
    const inFlight = clientAudits.some(a => a.status !== 'complete')
    if (!inFlight) {
      const completed = clientAudits.filter(a => a.status === 'complete')
      if (completed.length > 0) {
        const sorted = completed.slice().sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at))
        const days = daysSince(sorted[0].completed_at || sorted[0].updated_at)
        if (days !== null && days >= 75) {
          alerts.push({
            id: `audit-${client.id}`,
            type: 'audit_cadence',
            level: days >= 90 ? 'warning' : 'info',
            title: 'Quarterly SEO audit is due',
            message: `Last audit was ${days} days ago — quarterly audits should happen every 90 days.`,
            action: 'open_seo',
          })
        }
      }
    }
  }
  // Note: clients who have NEVER had an audit are flagged inside the SEO workspace
  // itself (since we don't want to show "no audit ever" alerts for clients that
  // don't get SEO services at all — the workspace alert handles that case)

  return alerts
}

/**
 * Bulk compute alerts for many clients at once. Returns a map keyed by client_id.
 * Useful for views that need to know which clients need attention without
 * iterating one at a time.
 */
export function computeAllClientAlerts(clients, blogs, batches, audits = []) {
  const byClient = new Map()
  const blogsByClient = new Map()
  const batchesByClient = new Map()
  const auditsByClient = new Map()

  for (const b of blogs) {
    if (!blogsByClient.has(b.client_id)) blogsByClient.set(b.client_id, [])
    blogsByClient.get(b.client_id).push(b)
  }
  for (const b of batches) {
    if (!batchesByClient.has(b.client_id)) batchesByClient.set(b.client_id, [])
    batchesByClient.get(b.client_id).push(b)
  }
  for (const a of audits) {
    if (!auditsByClient.has(a.client_id)) auditsByClient.set(a.client_id, [])
    auditsByClient.get(a.client_id).push(a)
  }

  for (const client of clients) {
    if (client.archived_at) continue
    const alerts = computeClientAlerts(
      client,
      blogsByClient.get(client.id) || [],
      batchesByClient.get(client.id) || [],
      auditsByClient.get(client.id) || [],
    )
    if (alerts.length > 0) byClient.set(client.id, alerts)
  }

  return byClient
}

/**
 * Small reusable AlertBanner component renderer-helper (just returns the JSX
 * pieces — the actual JSX is in the consuming components for styling control).
 * Exported as a constant so components can reuse colors consistently.
 */
export const ALERT_STYLES = {
  info: {
    background: '#fff8e1',
    border: '1px solid #f0d98a',
    color: '#8a6000',
    icon: '⏰',
  },
  warning: {
    background: '#fdf0f0',
    border: '1px solid #f0c4c4',
    color: '#8a3a3a',
    icon: '⚠️',
  },
}